/**
 * Lo que el servidor liquidó en un swap USDT→VES: los VES y la tasa que se
 * muestran en la pantalla final y en el texto para compartir.
 */
export interface LiquidacionUsdtVes {
  ves: number;
  tasa: number;
}

/**
 * El swap falló sin respuesta clara (status 0, 5xx, o un error que el
 * `ErrorInterceptor` global ya aplanó a string): la operación pudo haberse
 * creado igual. Lo marcan `errorSwapUsdtVes` (beneficiario) y
 * `WalletVesClientService.swapUsdtToVes` (wallet) con `transitorio: true`.
 * Los 400/422/429 llegan con `transitorio: false` y se pueden reintentar.
 */
export function esEnvioIncierto(error: any): boolean {
  return error?.transitorio === true;
}

/** Texto del aviso de resultado incierto, según el destino. */
export function mensajeEnvioIncierto(destino: 'wallet' | 'withdraw' | null): string {
  return destino === 'wallet'
    ? 'No pudimos confirmar si tu conversión se completó. Revisa tu historial antes de intentar de nuevo.'
    : 'No pudimos confirmar si tu envío se completó. Revisa tu historial antes de intentar de nuevo.';
}

function positivo(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Saca de la respuesta del swap lo que el servidor acreditó.
 *
 *  · `wallet` → `POST wallet-ves-client/swap-usdt-ves` responde
 *    `{ success, data: { operacion: { vesAcreditado, tasaAplicada } } }`.
 *    El servidor liquida con la tasa vigente, que puede no ser la cotizada.
 *  · `withdraw` → `POST binance-deposits/swap-usdt-to-ves` responde
 *    `{ success, message, swap: { vesAmount, rate } }`.
 *
 * Cada campo que falte o no sea un número positivo cae al valor que calculó
 * el cliente (`respaldo`), que es lo que se mostraba antes.
 */
export function liquidacionDeRespuesta(
  destino: 'wallet' | 'withdraw' | null,
  respuesta: any,
  respaldo: LiquidacionUsdtVes,
): LiquidacionUsdtVes {
  const fuente =
    destino === 'wallet'
      ? {
          ves: respuesta?.data?.operacion?.vesAcreditado,
          tasa: respuesta?.data?.operacion?.tasaAplicada,
        }
      : destino === 'withdraw'
        ? { ves: respuesta?.swap?.vesAmount, tasa: respuesta?.swap?.rate }
        : { ves: null, tasa: null };

  return {
    ves: positivo(fuente.ves) ?? respaldo.ves,
    tasa: positivo(fuente.tasa) ?? respaldo.tasa,
  };
}
